import { AlertTriangle, MailCheck, MailSearch, X } from 'lucide-react';
import pluralize from 'pluralize';
import { BorderBeam } from '../../../design-system/animations/border-beam';
import { Button } from '../../../design-system/button/button';
import { Inline } from '../../../design-system/inline/inline';
import { Stack } from '../../../design-system/stack/stack';
import { exhaustiveCheck } from '../../../utils/exhaustive-check';

export type CardVariant = 'EXTRACTING' | 'COMPLETE' | 'FAILED';

type InvoiceScanningProgressCardProps = {
  variant: CardVariant;
  invoiceCount: number;
  onHideCard: () => void;
};

export const InvoiceScanningProgressCard = (props: InvoiceScanningProgressCardProps) => {
  const { header, description, icon: Icon, isCloseIconVisible } = getCardContent(props);

  return (
    <Inline space={3} className="relative space-y-3 rounded-lg border-[0.5px] border-border bg-white px-4 py-4">
      {props.variant === 'EXTRACTING' && (
        <BorderBeam size={300} duration={3} className="from-[#CC50F2] via-[#EF7DFF] to-[#293CE9]" />
      )}

      {isCloseIconVisible && (
        <Button
          aria-label="Close scanning progress card"
          variant="ghost"
          size="icon"
          onClick={props.onHideCard}
          className="absolute top-2 right-2"
        >
          <X strokeWidth={1} className="h-4 w-4" />
        </Button>
      )}

      <div className="h-10 w-10 rounded-full bg-muted p-2.5">
        <Icon strokeWidth={1} className="h-full w-full" />
      </div>

      <Stack align="stretch">
        <h3 className="text-base leading-6 font-medium tracking-tight text-black">{header}</h3>

        <p className="text-xs leading-6 font-medium tracking-tight text-muted-foreground">{description}</p>
      </Stack>
    </Inline>
  );
};

const getCardContent = ({ variant, invoiceCount }: InvoiceScanningProgressCardProps) => {
  switch (variant) {
    case 'EXTRACTING': {
      const description =
        invoiceCount > 0
          ? `We're fetching invoices, checking if you've paid them or not, and matching them to past transactions. So far we've found ${invoiceCount} potential ${pluralize('invoice', invoiceCount)}.`
          : `We're fetching invoices, checking if you've paid them or not, and matching them to past transactions.`;

      return {
        header: 'Scanning your inbox for invoices',
        description,
        icon: MailSearch,
        isCloseIconVisible: true,
      };
    }
    case 'COMPLETE': {
      const description =
        invoiceCount > 0
          ? `We've finished scanning your Gmail and found ${invoiceCount} ${pluralize('invoice', invoiceCount)}.`
          : `We've finished scanning your Gmail and found no invoices.`;

      return {
        header: 'Inbox scan complete',
        description,
        icon: MailCheck,
        isCloseIconVisible: true,
      };
    }
    case 'FAILED': {
      return {
        header: 'Unfortunately we were unable to fetch any invoices',
        description: 'Please disconnect and reconnect your gmail connection before trying again.',
        icon: AlertTriangle,
        isCloseIconVisible: true,
      };
    }
    default:
      return exhaustiveCheck(variant);
  }
};
